/**
 * BrowserCompatibility
 * 
 * Provides feature detection and polyfills for browser compatibility.
 * Ensures the application works across Chrome, Firefox, Safari, and Edge.
 * 
 * Validates: Requirements 1.4, 1.5
 */

export interface CompatibilityReport {
  browser: string;
  version: string;
  features: {
    textEncoder: boolean;
    textDecoder: boolean;
    compressionStream: boolean;
    decompressionStream: boolean;
    localStorage: boolean;
    fetch: boolean;
  };
  warnings: string[];
  errors: string[];
}

export class BrowserCompatibility {
  private static instance: BrowserCompatibility;
  private compatibilityReport: CompatibilityReport | null = null;

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): BrowserCompatibility {
    if (!BrowserCompatibility.instance) {
      BrowserCompatibility.instance = new BrowserCompatibility();
    }
    return BrowserCompatibility.instance;
  }

  /**
   * Check if TextEncoder is available
   */
  hasTextEncoder(): boolean {
    return typeof TextEncoder !== 'undefined';
  }

  /**
   * Check if TextDecoder is available
   */
  hasTextDecoder(): boolean {
    return typeof TextDecoder !== 'undefined';
  }

  /**
   * Check if CompressionStream is available
   */
  hasCompressionStream(): boolean {
    return typeof CompressionStream !== 'undefined';
  }

  /**
   * Check if DecompressionStream is available
   */
  hasDecompressionStream(): boolean {
    return typeof DecompressionStream !== 'undefined';
  }

  /**
   * Check if localStorage is available
   */
  hasLocalStorage(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if Fetch API is available
   */
  hasFetch(): boolean {
    return typeof fetch !== 'undefined';
  }

  /**
   * Detect browser name and version
   */
  detectBrowser(): { name: string; version: string } {
    const userAgent = navigator.userAgent;
    
    // Chrome
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      const match = userAgent.match(/Chrome\/(\d+)/);
      return { name: 'Chrome', version: match ? match[1] : 'unknown' };
    }
    
    // Edge (Chromium-based)
    if (userAgent.includes('Edg')) {
      const match = userAgent.match(/Edg\/(\d+)/);
      return { name: 'Edge', version: match ? match[1] : 'unknown' };
    }
    
    // Firefox
    if (userAgent.includes('Firefox')) {
      const match = userAgent.match(/Firefox\/(\d+)/);
      return { name: 'Firefox', version: match ? match[1] : 'unknown' };
    }
    
    // Safari
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      const match = userAgent.match(/Version\/(\d+)/);
      return { name: 'Safari', version: match ? match[1] : 'unknown' };
    }
    
    return { name: 'Unknown', version: 'unknown' };
  }

  /**
   * Generate a comprehensive compatibility report
   */
  generateCompatibilityReport(): CompatibilityReport {
    if (this.compatibilityReport) {
      return this.compatibilityReport;
    }

    const browser = this.detectBrowser();
    const warnings: string[] = [];
    const errors: string[] = [];

    const features = {
      textEncoder: this.hasTextEncoder(),
      textDecoder: this.hasTextDecoder(),
      compressionStream: this.hasCompressionStream(),
      decompressionStream: this.hasDecompressionStream(),
      localStorage: this.hasLocalStorage(),
      fetch: this.hasFetch(),
    };

    // Check for critical missing features
    if (!features.textEncoder || !features.textDecoder) {
      errors.push('TextEncoder/TextDecoder not available. This is required for the application to function.');
    }

    if (!features.localStorage) {
      warnings.push('localStorage not available. Caching will be disabled.');
    }

    if (!features.fetch) {
      errors.push('Fetch API not available. This is required for the application to function.');
    }

    if (!features.compressionStream || !features.decompressionStream) {
      warnings.push('CompressionStream not available. Falling back to pako library for compression.');
    }

    this.compatibilityReport = {
      browser: browser.name,
      version: browser.version,
      features,
      warnings,
      errors,
    };

    return this.compatibilityReport;
  }

  /**
   * Check if the browser is supported
   */
  isBrowserSupported(): boolean {
    const report = this.generateCompatibilityReport();
    return report.errors.length === 0;
  }

  /**
   * Get minimum browser versions required
   */
  getMinimumVersions(): Record<string, string> {
    return {
      Chrome: '80',
      Firefox: '113',
      Safari: '16.4',
      Edge: '80',
    };
  }

  /**
   * Check if current browser meets minimum version requirements
   */
  meetsMinimumVersion(): boolean {
    const browser = this.detectBrowser();
    const minimumVersions = this.getMinimumVersions();
    
    if (!(browser.name in minimumVersions)) {
      return false;
    }

    const currentVersion = parseInt(browser.version, 10);
    const minimumVersion = parseInt(minimumVersions[browser.name], 10);

    return !isNaN(currentVersion) && currentVersion >= minimumVersion;
  }

  /**
   * Load polyfills for missing features
   */
  async loadPolyfills(): Promise<void> {
    const report = this.generateCompatibilityReport();

    // Check if we need TextEncoder/TextDecoder
    // Note: These are available in all modern browsers, so if they're missing,
    // the browser is too old to support this application
    if (!report.features.textEncoder || !report.features.textDecoder) {
      await this.loadTextEncodingPolyfill();
    }

    // CompressionStream polyfill is handled by pako library in CompressionService
    // No additional polyfill needed here
  }

  /**
   * Load TextEncoder/TextDecoder polyfill
   * @private
   */
  private async loadTextEncodingPolyfill(): Promise<void> {
    // Note: TextEncoder/TextDecoder are available in all modern browsers
    // (Chrome 38+, Firefox 19+, Safari 10.1+, Edge 79+)
    // If they're not available, the browser is too old to support this application
    
    if (typeof TextEncoder === 'undefined' || typeof TextDecoder === 'undefined') {
      console.error('TextEncoder/TextDecoder not available. Browser is too old.');
      throw new Error('TextEncoder/TextDecoder not available. Please upgrade to a modern browser.');
    }
    
    // No polyfill needed for modern browsers
    console.log('TextEncoder/TextDecoder are natively supported');
  }

  /**
   * Display compatibility warning to user if needed
   */
  displayCompatibilityWarning(): void {
    const report = this.generateCompatibilityReport();
    
    if (report.errors.length > 0) {
      const message = `Your browser (${report.browser} ${report.version}) is not fully supported.\n\nIssues:\n${report.errors.join('\n')}\n\nPlease upgrade to a modern browser.`;
      console.error(message);
      
      // Display a user-friendly warning banner
      this.createWarningBanner(message, 'error');
    } else if (report.warnings.length > 0) {
      const message = `Your browser (${report.browser} ${report.version}) has limited support.\n\nWarnings:\n${report.warnings.join('\n')}`;
      console.warn(message);
      
      // Display a user-friendly warning banner
      this.createWarningBanner(message, 'warning');
    }
  }

  /**
   * Create a warning banner in the UI
   * @private
   */
  private createWarningBanner(message: string, type: 'error' | 'warning'): void {
    // Check if banner already exists
    if (document.getElementById('browser-compatibility-banner')) {
      return;
    }

    const banner = document.createElement('div');
    banner.id = 'browser-compatibility-banner';
    banner.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      padding: 12px 20px;
      background-color: ${type === 'error' ? '#dc3545' : '#ffc107'};
      color: ${type === 'error' ? '#fff' : '#000'};
      text-align: center;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    `;

    const messageElement = document.createElement('div');
    messageElement.textContent = message.split('\n')[0]; // Show first line only
    messageElement.style.cssText = 'margin-bottom: 8px;';

    const closeButton = document.createElement('button');
    closeButton.textContent = 'Dismiss';
    closeButton.style.cssText = `
      background: rgba(255,255,255,0.2);
      border: 1px solid rgba(255,255,255,0.3);
      padding: 4px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      margin-left: 12px;
    `;
    closeButton.onclick = () => banner.remove();

    banner.appendChild(messageElement);
    banner.appendChild(closeButton);
    document.body.insertBefore(banner, document.body.firstChild);
  }

  /**
   * Log compatibility report to console
   */
  logCompatibilityReport(): void {
    const report = this.generateCompatibilityReport();
    
    console.group('🔍 Browser Compatibility Report');
    console.log(`Browser: ${report.browser} ${report.version}`);
    console.log('Features:');
    console.table(report.features);
    
    if (report.warnings.length > 0) {
      console.warn('Warnings:', report.warnings);
    }
    
    if (report.errors.length > 0) {
      console.error('Errors:', report.errors);
    }
    
    console.groupEnd();
  }

  /**
   * Initialize browser compatibility checks
   * Should be called early in application startup
   */
  async initialize(): Promise<void> {
    // Generate compatibility report
    this.generateCompatibilityReport();
    
    // Log report to console
    this.logCompatibilityReport();
    
    // Load polyfills if needed
    try {
      await this.loadPolyfills();
    } catch (error) {
      console.error('Failed to load polyfills:', error);
    }
    
    // Display warning if needed
    this.displayCompatibilityWarning();
  }
}

// Export singleton instance
export const browserCompatibility = BrowserCompatibility.getInstance();
